import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';

import { useGridVariant } from '~/builder/utils/gridVariantContext';
import useAssortmentTestConfigs from '~/shared/utils/useAssortmentTestConfigs';
import { useCioClient } from '~/shared/utils/useCioClient';
import useHasFeature, { featureNames } from '~/shared/utils/useHasFeature';
import useSecondarySite from '~/shared/utils/useSecondarySite';
import { useMembership } from '~/techstyle-shared/react-accounts';
import { useDomain } from '~/techstyle-shared/redux-core';

import getCioLeanFacetResults from '../api/getCioLeanFacetResults';
import { LEAN_FACET_STALE_TIME } from '../constants';
import { useGridMeta } from '../context/GridDataContexts';
import { getSelectedSortOption } from '../utils/sortOptions';

import useGridInputs from './useGridInputs';

/**
 * Shared params and base (unfiltered) lean facet query.
 *
 * React Query deduplicates by query key, so multiple consumers calling this
 * hook on the same page share a single network request via the
 * ['LeanFacets:base', sharedParams] cache entry.
 */
const useBaseFacets = () => {
  const cioClient = useCioClient();
  const { isVip, isElite } = useMembership();
  const { tld } = useDomain();
  const isSecondarySite = Boolean(useSecondarySite());
  const { enabled: removeOriginFlag } = useHasFeature(
    featureNames.REMOVE_ORIGIN
  );
  const { enabled: surfaceHiddenFacets } = useHasFeature(
    featureNames.SURFACE_HIDDEN_FACETS
  );
  const { isSaleGrid } = useGridVariant();
  const { excludeFplCodes, excludeTagIds } = useAssortmentTestConfigs();

  const {
    type: gridType,
    cioFilterName,
    cioFilterValue,
    searchQuery: metaSearchQuery,
  } = useGridMeta();

  const isCioBrowse = gridType === 'cio-browse';
  const isCioSearch = gridType === 'cio-search';
  const isCio = isCioBrowse || isCioSearch;

  const { asPath } = useRouter();
  const query = asPath.split('?')[1];
  const urlParams = new URLSearchParams(query);

  const searchQuery = metaSearchQuery || urlParams.get('q') || '';

  const { sortOptions } = useGridInputs();
  const selectedSortOption = getSelectedSortOption(urlParams, sortOptions);

  const queryType: 'search' | 'browse' = isCioSearch ? 'search' : 'browse';

  const sharedParams = useMemo(
    () => ({
      queryType,
      cioFilterName,
      cioFilterValue,
      searchQuery: isCioSearch ? searchQuery : undefined,
      isElite,
      isVip,
      selectedSortOption,
      tld,
      removeOriginFlag,
      isSecondarySite,
      isSaleGrid,
      excludeTagIds,
      excludeFplCodes,
      surfaceHiddenFacets,
    }),
    [
      queryType,
      cioFilterName,
      cioFilterValue,
      isCioSearch,
      searchQuery,
      isElite,
      isVip,
      selectedSortOption,
      tld,
      removeOriginFlag,
      isSecondarySite,
      isSaleGrid,
      excludeTagIds,
      excludeFplCodes,
      surfaceHiddenFacets,
    ]
  );

  const { data: baseFacetsData, isLoading: isBaseFacetsLoading } = useQuery({
    queryKey: ['LeanFacets:base', sharedParams],
    queryFn: () =>
      getCioLeanFacetResults({
        cioClient,
        ...sharedParams,
        selectedUrlFilters: {},
      }),
    enabled: Boolean(cioClient) && isCio,
    staleTime: LEAN_FACET_STALE_TIME,
  });

  return {
    baseFacets: baseFacetsData?.facets ?? null,
    baseTotalNumResults: baseFacetsData?.totalNumResults ?? null,
    isBaseFacetsLoading,
    sharedParams,
    cioClient,
    isCio,
  };
};

export default useBaseFacets;
